import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  ScrollText, Search, Calendar, RefreshCw, Loader2, Download, FileSpreadsheet, FileText, Receipt, FileMinus, CreditCard, AlertTriangle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/finance/statements`;

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString() : '—');

const TYPE_STYLE = {
  invoice:     { label: 'Invoice',     icon: FileText,   className: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  payment:     { label: 'Payment',     icon: CreditCard, className: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  credit_note: { label: 'Credit Note', icon: FileMinus,  className: 'bg-orange-100 text-orange-800 border-orange-200' },
};

const TypePill = ({ type }) => {
  const s = TYPE_STYLE[type] || { label: type, icon: Receipt, className: 'bg-slate-100 text-slate-700 border-slate-200' };
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold border ${s.className}`}>
      <Icon className="h-3 w-3" /> {s.label}
    </span>
  );
};

const errMsg = (e) => {
  const detail = e?.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg || JSON.stringify(d)).join('; ');
  if (typeof detail === 'string') return detail;
  return e?.message || '';
};

const GovStatements = () => {
  const { user } = useAuth();
  const isUske = user?.role === 'super_admin' || user?.role === 'admin';

  const [tab, setTab] = useState('transactions');
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [start, setStart] = useState(firstOfMonth.toISOString().slice(0, 10));
  const [end, setEnd] = useState('');
  const [q, setQ] = useState('');
  const [agencyId, setAgencyId] = useState(user?.agency_id || '');
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  // For USKE admins, load the list of agencies so they can pick.
  useEffect(() => {
    if (!isUske) return;
    axios.get(`${BACKEND_URL}/api/gov/agencies`)
      .then((r) => setAgencies(r.data || []))
      .catch(() => setAgencies([]));
  }, [isUske]);

  const canQuery = useMemo(() => (isUske ? !!agencyId : !!user?.agency_id), [isUske, agencyId, user]);

  const load = useCallback(async () => {
    if (!canQuery) return;
    setLoading(true);
    try {
      const params = {};
      if (isUske) params.agency_id = agencyId;
      if (start) params.start = start;
      if (end) params.end = end;
      if (q.trim()) params.q = q.trim();
      const r = await axios.get(`${API}/transactions`, { params });
      setData(r.data);
    } catch (e) {
      toast({ title: 'Statements load failed', description: errMsg(e), variant: 'destructive' });
      setData(null);
    } finally { setLoading(false); }
  }, [canQuery, isUske, agencyId, start, end, q]);

  useEffect(() => { load(); }, [agencyId, load]);

  const downloadFile = async (url, filename) => {
    try {
      const res = await axios.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(blobUrl);
      toast({ title: 'Downloaded', description: filename });
    } catch (e) {
      toast({ title: 'Download failed', description: errMsg(e), variant: 'destructive' });
    }
  };

  const exportExcel = () => {
    const params = new URLSearchParams();
    if (isUske && agencyId) params.set('agency_id', agencyId);
    if (start) params.set('start', start);
    if (end) params.set('end', end);
    if (q.trim()) params.set('q', q.trim());
    const name = `Transactions-${(data?.agency_name || 'Agency').replace(/\s+/g, '_')}.xlsx`;
    downloadFile(`${API}/transactions.xlsx?${params.toString()}`, name);
  };

  // Monthly statement PDF — uses whichever month `start` falls in (default this month).
  const downloadMonthlyPdf = () => {
    const d = start ? new Date(start) : new Date();
    const year = d.getUTCFullYear();
    const month = d.getUTCMonth() + 1;
    const params = new URLSearchParams();
    if (isUske && agencyId) params.set('agency_id', agencyId);
    params.set('year', year);
    params.set('month', month);
    const monthName = d.toLocaleString('en-US', { month: 'long' });
    const name = `Statement-${(data?.agency_name || 'Agency').replace(/\s+/g, '_')}-${monthName}_${year}.pdf`;
    downloadFile(`${API}/monthly.pdf?${params.toString()}`, name);
  };

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-4" data-testid="statements-page">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="p-2 bg-blue-100 rounded-lg"><ScrollText className="h-6 w-6 text-blue-700" /></div>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Account Statements</h1>
          <p className="text-sm text-slate-500">
            {isUske ? 'Select an agency to view its transaction history.' :
              (user?.role === 'agency_admin'
                ? 'Full agency-wide transaction history — invoices, payments, credit notes.'
                : 'Your scoped transaction history — division/department only.')}
          </p>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-2 border-b border-slate-200">
        <button onClick={() => setTab('transactions')} data-testid="stmt-tab-transactions"
          className={`px-3 py-1.5 text-sm border-b-2 -mb-px ${tab === 'transactions' ? 'border-blue-700 text-blue-700 font-medium' : 'border-transparent text-slate-500'}`}>
          Transactions
        </button>
        <button onClick={() => setTab('aging')} data-testid="stmt-tab-aging"
          className={`px-3 py-1.5 text-sm border-b-2 -mb-px ${tab === 'aging' ? 'border-blue-700 text-blue-700 font-medium' : 'border-transparent text-slate-500'}`}>
          Aging
        </button>
      </div>

      {tab === 'aging' ? (
        <AgingPanel isUske={isUske} agencyId={agencyId} setAgencyId={setAgencyId} agencies={agencies} />
      ) : (
      <>
      {/* Controls */}
      <Card className="border-2">
        <CardContent className="p-4 grid gap-3 grid-cols-1 md:grid-cols-6">
          {isUske && (
            <div className="md:col-span-2">
              <Label className="text-xs">Agency</Label>
              <select value={agencyId} onChange={(e) => setAgencyId(e.target.value)}
                className="w-full border rounded px-2 py-1.5 text-sm bg-white mt-1" data-testid="statements-agency-select">
                <option value="">— Select agency —</option>
                {agencies.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <Label className="text-xs">Start date</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1" data-testid="statements-start" />
          </div>
          <div>
            <Label className="text-xs">End date</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1" data-testid="statements-end" />
          </div>
          <div className={isUske ? 'md:col-span-2' : 'md:col-span-3'}>
            <Label className="text-xs">Search by Invoice #, Order #, or PO #</Label>
            <div className="relative mt-1">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. INV-1001, SO-100, PO-A" className="pl-8" data-testid="statements-search" onKeyDown={(e) => e.key === 'Enter' && load()} />
            </div>
          </div>
          <div className="md:col-span-6 flex flex-wrap gap-2">
            <Button size="sm" onClick={load} disabled={!canQuery || loading} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="statements-run">
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Run
            </Button>
            <div className="flex-1" />
            <Button size="sm" variant="outline" onClick={exportExcel} disabled={!data || loading} data-testid="statements-export-xlsx">
              <FileSpreadsheet className="h-4 w-4 mr-1" /> Export to Excel
            </Button>
            <Button size="sm" variant="outline" onClick={downloadMonthlyPdf} disabled={!canQuery || loading} data-testid="statements-monthly-pdf">
              <Download className="h-4 w-4 mr-1" /> Monthly Statement (PDF)
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryCard label="Opening Balance" value={money(data.opening_balance)} testId="stmt-opening" />
          <SummaryCard label="Total Charges"   value={money(data.totals.debit)}    testId="stmt-debit" className="text-indigo-700" />
          <SummaryCard label="Total Credits"   value={money(data.totals.credit)}   testId="stmt-credit" className="text-emerald-700" />
          <SummaryCard label="Closing Balance" value={money(data.closing_balance)} testId="stmt-closing" bold />
        </div>
      )}

      {/* Ledger */}
      <Card className="border-2">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="h-4 w-4 text-slate-500" />
            Transaction History {data?.scope?.start ? `· from ${fmtDate(data.scope.start)}` : ''} {data?.scope?.end ? `to ${fmtDate(data.scope.end)}` : ''}
            {data?.totals?.count != null && <span className="ml-2 text-xs font-normal text-slate-500">({data.totals.count} rows)</span>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-700" /></div>
          ) : !data ? (
            <div className="text-slate-400 text-sm py-4">{isUske ? 'Select an agency above.' : 'Loading…'}</div>
          ) : data.transactions.length === 0 ? (
            <div className="text-slate-400 text-sm py-4">No transactions in this period.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                  <tr>
                    <th className="text-left p-2">Date</th>
                    <th className="text-left p-2">Type</th>
                    <th className="text-left p-2">Doc #</th>
                    <th className="text-left p-2">Order #</th>
                    <th className="text-left p-2">Description</th>
                    <th className="text-right p-2">Charge</th>
                    <th className="text-right p-2">Credit</th>
                    <th className="text-right p-2">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t bg-slate-50/40">
                    <td className="p-2 text-slate-500 italic">—</td>
                    <td className="p-2" />
                    <td className="p-2" />
                    <td className="p-2" />
                    <td className="p-2 italic text-slate-500">Opening Balance</td>
                    <td className="p-2" />
                    <td className="p-2" />
                    <td className="p-2 text-right font-semibold tabular-nums" data-testid="ledger-opening">{money(data.opening_balance)}</td>
                  </tr>
                  {data.transactions.map((r) => (
                    <tr key={r.id + r.type} className="border-t hover:bg-slate-50/60" data-testid={`ledger-row-${r.id}`}>
                      <td className="p-2 whitespace-nowrap">{fmtDate(r.date)}</td>
                      <td className="p-2"><TypePill type={r.type} /></td>
                      <td className="p-2 font-mono text-xs">{r.doc_no || '—'}</td>
                      <td className="p-2 font-mono text-xs text-slate-500">{r.order_no || '—'}</td>
                      <td className="p-2 text-slate-700">{r.description}</td>
                      <td className="p-2 text-right tabular-nums text-indigo-700">{r.debit ? money(r.debit) : ''}</td>
                      <td className="p-2 text-right tabular-nums text-emerald-700">{r.credit ? money(r.credit) : ''}</td>
                      <td className="p-2 text-right font-semibold tabular-nums">{money(r.balance)}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-slate-300 bg-blue-50/40">
                    <td colSpan={5} className="p-2 font-semibold text-right">Closing Balance</td>
                    <td className="p-2 text-right font-semibold tabular-nums text-indigo-700">{money(data.totals.debit)}</td>
                    <td className="p-2 text-right font-semibold tabular-nums text-emerald-700">{money(data.totals.credit)}</td>
                    <td className="p-2 text-right font-bold tabular-nums" data-testid="ledger-closing">{money(data.closing_balance)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      </>
      )}
    </div>
  );
};

// -----------------------------------------------------------------------------
// Aging tab
// -----------------------------------------------------------------------------
const BUCKET_COLOR = {
  '0-30': 'bg-emerald-50 border-emerald-300 text-emerald-800',
  '31-60': 'bg-amber-50 border-amber-300 text-amber-800',
  '61-90': 'bg-orange-50 border-orange-300 text-orange-800',
  '90+':  'bg-red-50 border-red-300 text-red-800',
};

const AgingPanel = ({ isUske, agencyId, setAgencyId, agencies }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (isUske && !agencyId) return;
    setLoading(true);
    try {
      const params = {};
      if (isUske) params.agency_id = agencyId;
      const r = await axios.get(`${API}/aging`, { params });
      setData(r.data);
    } catch (e) {
      toast({ title: 'Aging load failed', description: errMsg(e), variant: 'destructive' });
      setData(null);
    } finally { setLoading(false); }
  }, [isUske, agencyId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="aging-panel">
      <Card className="border-2">
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          {isUske && (
            <div className="min-w-[220px]">
              <Label className="text-xs">Agency</Label>
              <select value={agencyId} onChange={(e) => setAgencyId(e.target.value)}
                className="w-full border rounded px-2 py-1.5 text-sm bg-white mt-1" data-testid="aging-agency-select">
                <option value="">— Select agency —</option>
                {agencies.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
          )}
          <div className="ml-auto">
            <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="aging-refresh">
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-700" /></div>
      ) : !data ? (
        <div className="text-slate-400 text-sm">{isUske ? 'Select an agency above.' : 'Loading…'}</div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {['0-30', '31-60', '61-90', '90+'].map((b) => (
              <Card key={b} className={`border-2 ${BUCKET_COLOR[b]}`}>
                <CardContent className="p-4">
                  <div className="text-xs uppercase font-semibold">{b} days</div>
                  <div className="text-2xl mt-1 tabular-nums font-bold" data-testid={`bucket-${b}`}>{money(data.buckets[b])}</div>
                  <div className="text-[11px] opacity-80 mt-0.5">{data.counts[b]} invoice{data.counts[b] === 1 ? '' : 's'}</div>
                </CardContent>
              </Card>
            ))}
          </div>
          <Card className="border-2">
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-600" /> Open Invoices ({data.invoices.length}) · Total {money(data.total_open)}</CardTitle></CardHeader>
            <CardContent>
              {data.invoices.length === 0 ? (
                <div className="text-sm text-slate-400 py-3">No outstanding invoices. Nice!</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                      <tr>
                        <th className="text-left p-2">Invoice #</th>
                        <th className="text-left p-2">Order #</th>
                        <th className="text-left p-2">Department</th>
                        <th className="text-left p-2">Issued</th>
                        <th className="text-left p-2">Due</th>
                        <th className="text-right p-2">Days Overdue</th>
                        <th className="text-left p-2">Bucket</th>
                        <th className="text-right p-2">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.invoices.map((r) => (
                        <tr key={r.invoice_id} className="border-t" data-testid={`aging-row-${r.invoice_id}`}>
                          <td className="p-2 font-mono text-xs">{r.invoice_no}</td>
                          <td className="p-2 font-mono text-xs text-slate-500">{r.sales_order_no || '—'}</td>
                          <td className="p-2">{r.department || '—'}</td>
                          <td className="p-2">{fmtDate(r.issue_date)}</td>
                          <td className="p-2">{fmtDate(r.due_date)}</td>
                          <td className="p-2 text-right tabular-nums">{r.days_overdue}</td>
                          <td className="p-2"><span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded border ${BUCKET_COLOR[r.bucket]}`}>{r.bucket}</span></td>
                          <td className="p-2 text-right font-semibold tabular-nums">{money(r.balance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

const SummaryCard = ({ label, value, testId, className = '', bold = false }) => (
  <Card className={`border-2 ${bold ? 'border-blue-300 bg-blue-50/40' : ''}`}>
    <CardContent className="p-4">
      <div className="text-xs uppercase text-slate-500">{label}</div>
      <div className={`text-2xl mt-1 tabular-nums ${bold ? 'font-bold text-blue-800' : `font-semibold ${className}`}`} data-testid={testId}>{value}</div>
    </CardContent>
  </Card>
);

export default GovStatements;
